import { ulid } from "ulidx";
import { OS } from "../vendor/sML.js";
import { invokeAsync } from "@/helpers/async";
import i18n from "../i18n";

import { localStorage } from "../../modules/shared-utils/storage";
import { decodeBase64, encodeBase64 } from "../../modules/shared-utils/base64";

const format = atob("cmF3") as "raw";
const key = new Uint8Array([
  113, 50, 99, 120, 168, 41, 23, 200, 27, 253, 50, 183, 110, 109, 145, 94, 29,
  75, 204, 66, 112, 135, 251, 193, 225, 200, 238, 134, 154, 114, 90, 17,
]);
const algoName = atob("QUVTLUNCQw==") as "AES-CBC";
const keyUsages = [
  atob("ZW5jcnlwdA==") as KeyUsage,
  atob("ZGVjcnlwdA==") as KeyUsage,
];
export const DEVICE_ID_KEY = "DM__device";

async function encrypt(key: CryptoKey, deviceClear: string): Promise<string> {
  const iv = await crypto.getRandomValues(new Uint8Array(16));
  const algo = { name: algoName, iv };
  const data = new TextEncoder().encode(deviceClear);
  const encData = await crypto.subtle.encrypt(algo, key, data);
  const ivText = encodeBase64(iv).replace(/=/g, "");
  const dataText = encodeBase64(encData).replace(/=/g, "");
  return `CR1-${ivText}:${dataText}`;
}

async function decrypt(key: CryptoKey, deviceRaw: string): Promise<string> {
  const frags = deviceRaw.split("-")[1].split(":");
  const iv = decodeBase64(frags[0]);
  const data = decodeBase64(frags[1]);
  const algo = { name: algoName, iv };
  const dec = await crypto.subtle.decrypt(algo, key, data);
  return new TextDecoder().decode(dec);
}

function readOrGenerateDeviceId(): Promise<string> {
  // disable rule because we have a try/catch over all operations
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const timeoutHandle = setTimeout(
        () => reject(new Error("timeout reading/generating device id")),
        3000
      );

      let currentDeviceId: string | null = null;
      const cryptoKey = await window.crypto.subtle.importKey(
        format,
        key,
        algoName,
        false,
        keyUsages
      );

      let needSave = false;
      const currentDeviceRaw = localStorage.getItem(DEVICE_ID_KEY);
      if (!currentDeviceRaw || !/[A-Z0-9]{3}-[\w=/]+/.test(currentDeviceRaw)) {
        // Generate new
        needSave = true;
        currentDeviceId = `WEB-${ulid()}`;
      } else {
        if (currentDeviceRaw.startsWith("WEB-")) {
          // Migrate from plaintext to encrypted storage
          needSave = true;
          currentDeviceId = currentDeviceRaw;
        } else {
          currentDeviceId = await decrypt(cryptoKey, currentDeviceRaw);
        }
      }

      if (needSave) {
        localStorage.setItem(
          DEVICE_ID_KEY,
          await encrypt(cryptoKey, currentDeviceId)
        );
      }

      clearTimeout(timeoutHandle);
      resolve(currentDeviceId);
    } catch (e) {
      reject(e);
    }
  });
}

function generateDeviceName(): string {
  const { t } = i18n.global;
  const deviceType =
    Object.keys(OS).length > 0
      ? ` - ${t("reader.device")}: ${Object.keys(OS)[0]} `
      : "";
  return t("title") + deviceType;
}

export class Device {
  private _id: Promise<string>;
  private _name: string;

  constructor() {
    this._id = readOrGenerateDeviceId();
    this._name = generateDeviceName();
    invokeAsync(() => this._id);
  }

  get id(): Promise<string> {
    return this._id;
  }

  async rawId(): Promise<string> {
    await this._id; // Ensure the ID is generated before returning

    return localStorage.getItem(DEVICE_ID_KEY);
  }

  get name(): string {
    return this._name;
  }
}

export default new Device();
